import { HttpEvent, HttpHandlerFn, HttpRequest } from "@angular/common/http";
import { inject } from "@angular/core";
import { Router } from "@angular/router";
import { catchError, Observable, throwError } from "rxjs";
import { AuthService } from "../../../app-legacy/core/services/auth.service";
import { DEFAULT_LANGUAGE } from "../constants/constants";
import { CURRENT_LANGUAGE_KEY } from "../constants/storage-keys";
import { GoogleAnalyticsLoggingService } from "../services/google-analytics-logging.service";

/**
 * Endpoints where a 401 means "these credentials are wrong", not "your session has expired".
 * Tearing the session down for these is both pointless — there is no session yet — and harmful:
 * it wipes storage and navigates away from the very form that needs to show the error.
 */
const CREDENTIAL_ENDPOINTS = /\/(login|request_2fa|check|forgot_username|reset-password|request-password-reset)(\/|\?|$)/i;

/**
 * Guards against a burst of 401s each tearing the session down independently. The route guard
 * fans out several calls at once, so without this a single expiry produced multiple
 * `localStorage.clear()` + `router.navigate()` pairs racing each other.
 */
let logoutInFlight = false;

export function authInterceptor(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
	const authService = inject(AuthService);
	const router = inject(Router);
	const analytics = inject(GoogleAnalyticsLoggingService);
	const token = authService.getAccessToken();
	const language = localStorage.getItem(CURRENT_LANGUAGE_KEY) || DEFAULT_LANGUAGE;

	if (token) {
		request = request.clone({
			setHeaders: {
				Authorization: `Bearer ${token}`,
				"Accept-Language": language,
			},
		});
	} else {
		request = request.clone({
			setHeaders: {
				"Accept-Language": language,
			},
		});
	}

	return next(request).pipe(
		catchError((error) => {
			if (error.status === 401) {
				const isCredentialCheck = CREDENTIAL_ENDPOINTS.test(request.url);
				const alreadyOnLogin = router.url.startsWith("/auth");

				// Record every forced logout before acting on it. Roughly one login in four is
				// followed by another within two minutes, and the trigger has never been captured —
				// this is the missing evidence: which call 401'd, and whether a token was even sent.
				if (!isCredentialCheck) {
					analytics.logForcedLogout({
						endpoint: request.url.replace(/^https?:\/\/[^/]+/, "").split("?")[0],
						method: request.method,
						hadToken: !!token,
						route: router.url,
						suppressed: alreadyOnLogin || logoutInFlight,
					});
				}

				if (!isCredentialCheck && !alreadyOnLogin && !logoutInFlight) {
					logoutInFlight = true;
					try {
						authService.logOut(false);
					} finally {
						// Released on a macrotask so the concurrent failures from the same fan-out are
						// covered, without wedging the flag if navigation throws.
						setTimeout(() => (logoutInFlight = false), 0);
					}
				}
			}

			// A failure below the HTTP layer (DNS, CORS rejection, connection refused) reports
			// status 0 and carries a ProgressEvent rather than a body, so spreading it yields no
			// `message` at all — every consumer then renders a blank error. Name it instead.
			// Deliberately NOT adding the transport status here. Consumers already branch on a
			// `status` the server puts in its own body (items-code-list treats 400 as "no config yet"),
			// so stamping the HTTP status would both overwrite that envelope value and make such
			// branches fire for failures that previously fell through to a visible error.
			const body = error.status === 0 ? {} : error.error;
			const customError = { ...body, error: true };

			// Only speak up when the server said nothing usable. Several callers render
			// `error.message || error.detail` (and the upload screens read `errorList`), so naming a
			// message unconditionally would mask the server's own, more specific text.
			//
			// This carries an i18n KEY, never resolved text. Translating here would mean injecting
			// TranslateService into the interceptor, and TranslateService is built from a loader that
			// depends on HttpClient — so resolving it while HttpClient is handling the very request
			// that loads the default language is a dependency cycle. Doing that broke translation
			// app-wide once already. The display layer resolves the key instead.
			// `errorList?.length`, not the bare array: an empty `errorList` is truthy, and the upload
			// screens render `error.errorList || [error.message]`, so treating `[]` as "the server said
			// something" would short-circuit that fallback and show nothing at all.
			if (!customError.message && !customError.detail && !customError.errorList?.length) {
				customError.messageKey = error.status === 0 ? "serverUnreachable" : "requestFailed";
			}

			return throwError(() => customError);
		}),
	);
}
